import { Injectable, signal, computed, effect } from '@angular/core';
import { HttpClient, HttpParams, httpResource } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { apiEndpoints } from '../app/configs/api-endpoints.config';
import { environment } from '../app/environments/environment';
import { PaciensVmForList, PaciensVmForEdit, PaciensVmForDetail, PaciensVmForCreate, PagedResultOfPaciensVmForList } from '../models/paciens.model';

@Injectable({
    providedIn: 'root',
})
export class PaciensService {
    private paciensEndpointUrl = apiEndpoints.paciens;

    private _pageNumber = signal<number>(0);
    private _pageSize = signal<number>(environment.paging.defaultPageSize);
    private _searchQuery = signal<string | undefined>(undefined);
    private _orderByPropertyName = signal<string | undefined>('nev');
    private _orderByReverse = signal<boolean | undefined>(undefined);

    private paciensResource = httpResource<PagedResultOfPaciensVmForList>( () => {
        let params = new HttpParams()
            .set('PageNumber', this._pageNumber().toString())
            .set('PageSize', this._pageSize().toString());

        if (this._searchQuery()) {
            params = params.set('SearchQuery', this._searchQuery()!);
        }

        if (this._orderByPropertyName()) {
            params = params.set('OrderByPropertyName', this._orderByPropertyName()!);
        }

        if (this._orderByReverse() !== undefined) {
            params = params.set('OrderByReverse', this._orderByReverse()!.toString());
        }

        return {
            url: this.paciensEndpointUrl, params: params
        };
    });

    readonly paciensek = computed((): PaciensVmForList[] => this.paciensResource.value()?.items ?? []);
    readonly loading = computed(() => this.paciensResource.isLoading());
    readonly error = computed(() => this.paciensResource.error()?.message ?? null);
    readonly totalItems = computed(() => this.paciensResource.value()?.totalItems ?? 0);
    readonly pageNumber = computed(() => this.paciensResource.value()?.pageNumber ?? this._pageNumber());
    readonly pageSize = computed(() => this.paciensResource.value()?.pageSize ?? this._pageSize());

    constructor(private http: HttpClient) {
        // Log when backend returns paciens data
        effect(() => {
            const data = this.paciensResource.value();
            if (data) {
                console.log('Backend returned paciens data:', {
                    totalItems: data.totalItems,
                    pageNumber: data.pageNumber,
                    pageSize: data.pageSize,
                    itemsCount: data.items?.length ?? 0,
                    items: data.items
                });
            }
        });
    }

    loadPaciensek(pageNumber: number, pageSize: number, searchQuery?: string, orderByPropertyName?: string, orderByReverse?: boolean): void {
        this._pageNumber.set(pageNumber);
        this._pageSize.set(pageSize);
        this._searchQuery.set(searchQuery);
        this._orderByPropertyName.set(orderByPropertyName);
        this._orderByReverse.set(orderByReverse);
    }

    getPaciensById(id: number): Observable<PaciensVmForDetail> {
        return this.http.get<PaciensVmForDetail>(`${this.paciensEndpointUrl}/${id}`);
    }

    addPaciens(paciens: PaciensVmForCreate): Observable<number> {
        return this.http.post<number>(this.paciensEndpointUrl, paciens);
    }

    updatePaciens(paciens: PaciensVmForEdit): void {
        this.http
            .put<PaciensVmForEdit>(`${this.paciensEndpointUrl}/${paciens.id}`, paciens)
            .pipe(
                tap(() => {
                    this.paciensResource.reload();
                })
            )
            .subscribe();
    }

    // deletePaciens(id: number): void {
    //     this._loading.set(true);
    //     this._error.set(null);

    //     this.http
    //         .delete<void>(`${this.paciensEndpointUrl}/${id}`)
    //         .pipe(
    //             tap(() => {
    //                 this._paciensek.update((items) =>
    //                     items.filter((item) => item.id !== id)
    //                 );
    //                 this._loading.set(false);
    //             }),
    //             catchError(this.handleError)
    //         )
    //         .subscribe();
    // }

}
